import type {
  CourseResponse,
  CourseWithStatsResponse,
  CreateCourseInput,
  PaginationMeta,
  UpdateCourseInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PaginatedResponse } from '@/lib/api'
import { api, buildQuery } from '@/lib/api'

interface CourseFilters {
  page?: string
  limit?: string
  search?: string
  categoryId?: string
  level?: string
  isPublished?: string
}

export interface CourseWithStats extends CourseWithStatsResponse {}

export interface PaginatedCoursesResponse {
  data: CourseWithStatsResponse[]
  pagination: PaginationMeta
}

export function useCourses(filters?: CourseFilters) {
  return useQuery({
    queryKey: ['courses', filters],
    queryFn: () =>
      api.getFull<PaginatedResponse<CourseWithStatsResponse>>('/api/courses', {
        limit: '100',
        ...filters,
      }),
  })
}

/**
 * Fetch courses with stats (lessonCount, enrollmentCount, totalDuration) and pagination info.
 * Returns the full API response including pagination metadata.
 */
export function useCoursesWithStats(filters?: CourseFilters) {
  return useQuery<PaginatedCoursesResponse>({
    queryKey: ['courses', 'withStats', filters],
    queryFn: async () => {
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const token = localStorage.getItem('auth_token')
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const res = await fetch(`${API_BASE}/api/courses${buildQuery(filters)}`, { headers })
      if (!res.ok) {
        throw new Error('Failed to fetch courses')
      }
      const body = await res.json()
      return { data: body.data, pagination: body.pagination }
    },
  })
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: ['courses', id],
    queryFn: () => api.get<CourseResponse>(`/api/courses/${id}`),
    enabled: !!id,
  })
}

export function useCourseLessons(courseId: string) {
  return useQuery({
    queryKey: ['courses', courseId, 'lessons'],
    queryFn: () => api.get<string[]>(`/api/courses/${courseId}/lessons`),
    enabled: !!courseId,
  })
}

export function useCreateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCourseInput & { tagIds?: string[] }) =>
      api.post<CourseResponse>('/api/courses', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  })
}

export function useUpdateCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCourseInput & { tagIds?: string[] } }) =>
      api.patch<CourseResponse>(`/api/courses/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  })
}

export function useDeleteCourse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/courses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
  })
}
